import { useState, useEffect } from "react";
import { Spinner } from "./UI";

export default function LiveDataBanner({
  hasApiKey, loading, errors, lastFetch, onRefresh,
}) {
  const [secsAgo, setSecsAgo] = useState(0);

  // Tick every 500ms so the "X s ago" counter stays fresh
  useEffect(() => {
    const t = setInterval(() => {
      setSecsAgo(lastFetch ? Math.round((Date.now() - lastFetch.getTime()) / 1000) : 0);
    }, 500);
    return () => clearInterval(t);
  }, [lastFetch]);

  // ── No API key ────────────────────────────────────────
  if (!hasApiKey) {
    return (
      <div style={banner("#FFB80010", "#FFB80030")}>
        <span style={{ color: "#FFB800", fontSize: 10, letterSpacing: "0.1em" }}>
          ⚠ STATIC DATA MODE
        </span>
        <span style={{ color: "#888888", fontSize: 10, marginLeft: 12 }}>
          Add <code style={code}>REACT_APP_FMP_KEY=your_key</code> to a{" "}
          <code style={code}>.env</code> file in the project root, then restart{" "}
          <code style={code}>npm start</code> to enable live market data.
        </span>
        <a
          href="https://financialmodelingprep.com/register"
          target="_blank"
          rel="noreferrer"
          style={{ marginLeft: "auto", color: "#FFB800", fontSize: 10, letterSpacing: "0.06em" }}
        >
          Get free API key →
        </a>
      </div>
    );
  }

  // ── Loading / first fetch ─────────────────────────────
  if (loading && !lastFetch) {
    return (
      <div style={banner("#3ddc840a", "#3ddc8420")}>
        <Spinner />
        <span style={{ color: "#888888", fontSize: 10, marginLeft: 10, letterSpacing: "0.06em" }}>
          Fetching live market data…
        </span>
      </div>
    );
  }

  // ── Partial errors ────────────────────────────────────
  if (errors && errors.length > 0) {
    return (
      <div style={banner("#FF445510", "#FF445530")}>
        <span style={{ color: "#FF4455", fontSize: 10, letterSpacing: "0.1em" }}>
          ⚠ LIVE DATA PARTIAL
        </span>
        <span style={{ color: "#888888", fontSize: 10, marginLeft: 12 }}>
          Could not fetch: {errors.join(", ")} — showing static fallback values.
        </span>
        <button onClick={onRefresh} style={refreshBtn("#FF445540", "#FF4455")}>
          RETRY
        </button>
      </div>
    );
  }

  // ── Live + refreshing ─────────────────────────────────
  return (
    <div style={banner("#3ddc840a", "#3ddc8418")}>

      {/* Pulse dot */}
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: loading ? "#FFB800" : "#3ddc84",
        boxShadow: loading ? "0 0 5px #FFB800" : "0 0 6px #3ddc8499",
        display: "inline-block",
        flexShrink: 0,
      }} />

      <span style={{
        color: loading ? "#FFB800" : "#3ddc84",
        fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
      }}>
        {loading ? "UPDATING…" : "LIVE"}
      </span>

      <span style={{ color: "#888888", fontSize: 10, letterSpacing: "0.04em" }}>
        Yahoo Finance · refreshes every{" "}
        <span style={{ color: "#3ddc84", fontWeight: 600 }}>2 s</span>
      </span>

      <span style={{ color: "#44444455", fontSize: 10 }}>
        Prices delayed ~15 min (Yahoo free tier)
      </span>

      {lastFetch && (
        <span style={{ color: "#44444444", fontSize: 10 }}>
          Last sync {secsAgo}s ago
        </span>
      )}

      <button
        onClick={onRefresh}
        disabled={loading}
        style={{
          ...refreshBtn("#3ddc8430", "#888888"),
          marginLeft: "auto",
          display: "flex", alignItems: "center", gap: 6,
          opacity: loading ? 0.5 : 1,
          cursor: loading ? "default" : "pointer",
        }}
        onMouseEnter={e => { if (!loading) e.currentTarget.style.color = "#3ddc84"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#888888"; }}
      >
        {loading ? <><Spinner /> Refreshing</> : "↻ REFRESH"}
      </button>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────
const banner = (bg, border) => ({
  display: "flex",
  alignItems: "center",
  padding: "5px 24px",
  background: bg,
  borderBottom: `1px solid ${border}`,
  gap: 12,
  minHeight: 30,
  flexWrap: "wrap",
});

const refreshBtn = (borderColor, color) => ({
  background: "transparent",
  border: `1px solid ${borderColor}`,
  color,
  padding: "2px 10px",
  borderRadius: 2,
  fontFamily: "inherit",
  fontSize: 10,
  letterSpacing: "0.08em",
  transition: "color 0.12s",
  cursor: "pointer",
});

const code = {
  background: "#111111",
  color: "#f0f0f0",
  padding: "1px 5px",
  borderRadius: 2,
  fontSize: 10,
  fontFamily: "inherit",
  border: "1px solid #152010",
};
